import React, { useState } from "react";
import { motion } from "framer-motion";
import { PhoneCall, Mail, MapPin, ArrowUpRight, Check, MessageCircle, Send as SendIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import {
  COMPANY,
  FEATURE_OPTIONS,
  EMPLOYEE_RANGES,
  CONTACT_WINDOWS,
} from "@/lib/constants";
import { fadeUp, staggerParent, slideLeft } from "@/lib/motion";

const initialForm = {
  full_name: "",
  company: "",
  email: "",
  phone: "",
  employees: "",
  current_provider: "",
  features_needed: [],
  preferred_contact_time: "",
  message: "",
};

const inputCls =
  "bg-white border-ink text-ink focus:border-ink focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-0 h-11 rounded-xl";
const selectTriggerCls =
  "bg-white border-ink text-ink h-11 rounded-xl focus:ring-2 focus:ring-coral";

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleFeature = (feat) =>
    setForm((f) => ({
      ...f,
      features_needed: f.features_needed.includes(feat)
        ? f.features_needed.filter((x) => x !== feat)
        : [...f.features_needed, feat],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const required = [
      "full_name",
      "company",
      "email",
      "phone",
      "employees",
      "preferred_contact_time",
    ];
    for (const k of required) {
      if (!form[k] || !String(form[k]).trim()) {
        setError("Please complete all required fields.");
        return;
      }
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      setSubmitted(true);
      setForm(initialForm);
      toast.success("Inquiry sent — we'll reach out within one business day.");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-24 sm:pt-32 overflow-hidden">
      <section className="relative py-12 sm:py-20 grain">
        <div className="container-x relative grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* LEFT */}
          <motion.div
            initial="hidden"
            animate="show"
            variants={staggerParent}
            className="lg:col-span-5"
          >
            <motion.div variants={fadeUp} className="text-overline">
              Contact
            </motion.div>
            <motion.h1
              variants={fadeUp}
              className="mt-4 font-display text-6xl sm:text-8xl text-ink tracking-[-0.04em] leading-[0.9]"
            >
              Tell us about your phone system.
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="mt-6 text-lg text-ink-2 leading-relaxed max-w-md"
            >
              Send a quick inquiry — we'll reply with a tailored quote and
              architecture within one business day.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-10 space-y-6">
              <ContactRow
                icon={PhoneCall}
                label="Phone"
                value={COMPANY.phone}
                sub="Mon–Fri · 8am – 6pm CT"
                href={`tel:+${COMPANY.phoneRaw}`}
                tint="bg-lime-pop"
                testid="contact-phone-link"
              />
              <ContactRow
                icon={MessageCircle}
                label="WhatsApp"
                value={COMPANY.whatsapp}
                sub="Chat with a specialist"
                href={`https://wa.me/${COMPANY.whatsappRaw}`}
                external
                tint="bg-white"
                testid="contact-whatsapp-link"
              />
              <ContactRow
                icon={SendIcon}
                label="Telegram"
                value={COMPANY.telegram}
                sub="Message us on Telegram"
                href={COMPANY.telegramUrl}
                external
                tint="bg-coral"
                testid="contact-telegram-link"
              />
              <ContactRow
                icon={Mail}
                label="Email"
                value={COMPANY.email}
                sub="Replies within one business day"
                href={`mailto:${COMPANY.email}`}
                tint="bg-white"
                testid="contact-email-link"
              />
              <ContactRow
                icon={MapPin}
                label="Coverage"
                value="United States — nationwide"
                sub="Remote deployment & on-site engineering"
                tint="bg-lime-pop"
              />
            </motion.div>
          </motion.div>

          {/* RIGHT — dramatic dark form card */}
          <motion.div
            initial="hidden"
            animate="show"
            variants={slideLeft}
            className="lg:col-span-7"
          >
            <div
              className="rounded-[2rem] bg-ink text-white p-6 sm:p-10 shadow-[0_40px_80px_-30px_rgba(10,10,10,0.35)]"
              data-testid="inquiry-form-card"
            >
              {submitted ? (
                <div className="text-center py-16" data-testid="inquiry-success">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 260, damping: 18 }}
                    className="mx-auto h-16 w-16 rounded-full bg-lime-pop text-ink flex items-center justify-center"
                  >
                    <Check className="h-7 w-7" strokeWidth={3} />
                  </motion.div>
                  <h2 className="mt-6 font-display text-3xl sm:text-4xl tracking-tight">
                    Inquiry received.
                  </h2>
                  <p className="mt-3 text-white/70 max-w-md mx-auto">
                    Thanks — a Mavericks PBX engineer will respond within one
                    business day with next steps.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-lime-pop hover:text-lime-pop px-6 h-11 text-sm transition-all"
                    data-testid="inquiry-send-another-btn"
                  >
                    Send another inquiry
                    <ArrowUpRight className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                  data-testid="inquiry-form"
                >
                  <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-lime-pop font-mono">
                      Start an inquiry
                    </div>
                    <h2 className="mt-2 font-display text-3xl sm:text-4xl tracking-tight">
                      Project details
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <DarkField label="Full name *" id="full_name">
                      <Input
                        id="full_name"
                        value={form.full_name}
                        onChange={(e) => update("full_name", e.target.value)}
                        className={inputCls}
                        data-testid="input-full-name"
                        required
                      />
                    </DarkField>
                    <DarkField label="Company *" id="company">
                      <Input
                        id="company"
                        value={form.company}
                        onChange={(e) => update("company", e.target.value)}
                        className={inputCls}
                        data-testid="input-company"
                        required
                      />
                    </DarkField>
                    <DarkField label="Work email *" id="email">
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        className={inputCls}
                        data-testid="input-email"
                        required
                      />
                    </DarkField>
                    <DarkField label="Phone *" id="phone">
                      <Input
                        id="phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        className={inputCls}
                        data-testid="input-phone"
                        required
                      />
                    </DarkField>

                    <DarkField label="Number of employees *" id="employees">
                      <Select
                        value={form.employees}
                        onValueChange={(v) => update("employees", v)}
                      >
                        <SelectTrigger
                          className={selectTriggerCls}
                          data-testid="select-employees"
                        >
                          <SelectValue placeholder="Select range" />
                        </SelectTrigger>
                        <SelectContent>
                          {EMPLOYEE_RANGES.map((r) => (
                            <SelectItem
                              key={r}
                              value={r}
                              data-testid={`select-employees-option-${r.replace(/\s+/g, "")}`}
                            >
                              {r}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </DarkField>

                    <DarkField label="Current phone provider" id="current_provider">
                      <Input
                        id="current_provider"
                        value={form.current_provider}
                        onChange={(e) => update("current_provider", e.target.value)}
                        placeholder="e.g. RingCentral, on-prem"
                        className={inputCls + " placeholder:text-ink/40"}
                        data-testid="input-current-provider"
                      />
                    </DarkField>
                  </div>

                  <DarkField label="Preferred contact time *" id="contact-time">
                    <Select
                      value={form.preferred_contact_time}
                      onValueChange={(v) => update("preferred_contact_time", v)}
                    >
                      <SelectTrigger
                        className={selectTriggerCls}
                        data-testid="select-contact-time"
                      >
                        <SelectValue placeholder="When should we reach you?" />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTACT_WINDOWS.map((w) => (
                          <SelectItem
                            key={w}
                            value={w}
                            data-testid={`select-contact-time-option-${w.split(" ")[0]}`}
                          >
                            {w}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </DarkField>

                  <div>
                    <Label className="text-sm font-medium text-white/80 mb-3 block">
                      Features needed
                    </Label>
                    <div
                      className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                      data-testid="features-checkbox-grid"
                    >
                      {FEATURE_OPTIONS.map((feat) => {
                        const checked = form.features_needed.includes(feat);
                        return (
                          <label
                            key={feat}
                            className={`flex items-center gap-3 rounded-xl border px-3.5 py-2.5 text-sm cursor-pointer transition-all ${
                              checked
                                ? "border-lime-pop bg-lime-pop text-ink"
                                : "border-white/15 bg-white/5 text-white/80 hover:bg-white/10"
                            }`}
                            data-testid={`feature-option-${feat.replace(/\W+/g, "-").toLowerCase()}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleFeature(feat)}
                              className={`border-white/40 ${checked ? "border-ink data-[state=checked]:bg-ink" : ""}`}
                            />
                            {feat}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <DarkField label="Anything else we should know?" id="message">
                    <Textarea
                      id="message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      rows={4}
                      placeholder="Sites, call volume peaks, integrations, timeline..."
                      className="bg-white border-ink text-ink focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-0 rounded-xl placeholder:text-ink/40"
                      data-testid="input-message"
                    />
                  </DarkField>

                  {error && (
                    <div
                      className="text-sm text-coral bg-coral/10 border border-coral/40 rounded-xl px-4 py-3"
                      data-testid="inquiry-error"
                    >
                      {error}
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-4 flex-wrap pt-2">
                    <p className="text-xs text-white/50 font-mono">
                      * Required fields
                    </p>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="btn-lime h-12 px-8 group text-base"
                      data-testid="inquiry-submit-btn"
                    >
                      {submitting ? "Sending..." : "Send Inquiry"}
                      <ArrowUpRight className="h-5 w-5 transition-transform group-hover:rotate-45" />
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, sub, href, tint = "bg-white", external, testid }) {
  const Comp = href ? "a" : "div";
  const extraProps = external ? { target: "_blank", rel: "noopener noreferrer" } : {};
  return (
    <Comp
      href={href}
      {...extraProps}
      className="group flex items-start gap-4"
      data-testid={testid}
    >
      <div
        className={`${tint} h-12 w-12 rounded-2xl border border-ink flex items-center justify-center shrink-0 group-hover:-rotate-6 transition-transform`}
      >
        <Icon className="h-5 w-5 text-ink" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-coral font-semibold">
          {label}
        </div>
        <div className="text-ink font-display text-xl mt-0.5 break-all">
          {value}
        </div>
        <div className="text-xs text-muted-warm font-mono mt-1">{sub}</div>
      </div>
    </Comp>
  );
}

function DarkField({ label, id, children }) {
  return (
    <div className="flex flex-col">
      <Label htmlFor={id} className="text-sm font-medium text-white/80 mb-2">
        {label}
      </Label>
      {children}
    </div>
  );
}
